import React from "react";
import PropTypes from "prop-types";

import { MODULE_MAPPING } from "base/constants";

window.loadedRemotes = [];
window.loadingScripts = {};
window.dependent_versionNumber = window.dependent_versionNumber || {};
let remoteScriptobj = {};

async function initializeRemote(remote) {
  // Initializes the share scope. This fills it with known provided modules from this build and all remotes
  // eslint-disable-next-line no-undef
  await __webpack_init_sharing__("default");
  let container = window[remote];
  if (MODULE_MAPPING[remote]) {
    console.info("module mapped", MODULE_MAPPING[remote]);
    container = window[MODULE_MAPPING[remote]];
  }
  // Initialize the container, it may provide shared modules
  // eslint-disable-next-line no-undef
  await container.init(__webpack_share_scopes__.default);
  console.info(`Initializing remote module - ${remote}`);
}

function loadComponent(module, componentName = "default") {
  let [remote, ...moduleName] = module.split("/");
  // eslint-disable-next-line space-before-function-paren
  return async () => {
    await initializeRemote(remote);
    const factory = await window[remote].get(`./${moduleName.join("/")}`);
    const Module = factory();
    return Module[componentName];
  };
}

const useDynamicScript = (remote, dependsOn, compName) => {
  const [ready, setReady] = React.useState(false);
  const [failed, setFailed] = React.useState(false);
  const remoteObj = React.useMemo(
    function iniitRemote() {
      return {
        urls: dependsOn.map((remote) => {
          return `${window.publicPath}static/${remote}/web/${window.dependent_versionNumber[remote]}/external.js`;
        }),
        names: dependsOn,
        baseRemote: remote
      };
    },
    [dependsOn, remote]
  );
  const isBaseScriptLoaded = React.useRef(true);

  React.useEffect(function initScripts() {
    async function loadScripts() {
      function downloadScript(url, remote) {
        return () =>
          new Promise((resolve, reject) => {
            const element = document.createElement("script");

            element.src = url;
            element.type = "text/javascript";
            //set async, only when the module is not dependent on other module.
            element.async = remoteObj.urls.length === 0;

            // eslint-disable-next-line space-before-function-paren
            element.onload = async () => {
              console.info(
                `Dynamic Script Loaded: ${url}`,
                remoteObj.urls,
                compName
              );
              //make sure all the moduels externels are loaded
              if (remote) {
                if (!window.loadedRemotes.includes(remote))
                  window.loadedRemotes.push(remote);
                await initializeRemote(remote);
              }
              resolve();
            };

            element.onerror = () => {
              console.warn(`Dynamic Script Error: ${url}`);
              setReady(false);
              setFailed(true);
              reject();
            };

            document.head.appendChild(element);
          });
      }

      function delay(delayInms) {
        return new Promise((resolve) => {
          setTimeout(() => {
            resolve(2);
          }, delayInms);
        });
      }

      function getLoadedRemoteIndex(remote) {
        return window.loadedRemotes.findIndex((url) => url === remote);
      }

      let downloadBaseScript = false;
      let remoteBaseUrl = `${window.publicPath}static/${
        remoteObj.baseRemote
      }/web/${
        window.dependent_versionNumber[remoteObj.baseRemote]
      }/external.js`;
      let baseRemoteLoading = false;

      /** download dependency scripts */
      const remoteScripts = remoteObj.urls.map((url, index) => {
        const remoteElement =
          window.loadedRemotes.includes(remoteObj.names[index]) ||
          window.loadingScripts[remoteObj.names[index]];
        if (!remoteElement) {
          downloadBaseScript = true;
          remoteScriptobj[remoteObj.names[index]] = true;
          window.loadingScripts[remoteObj.names[index]] = true;
          window.loadingScripts[remoteObj.baseRemote] = true;
          baseRemoteLoading = true;
          return {
            func: downloadScript(url, remoteObj.names[index])
          };
        } else {
          /** need to redownload base if it is downloaded before its dependencies */
          const dependencyRemoteOrder = getLoadedRemoteIndex(
            remoteObj.names[index]
          );
          const baseRemoteOrder = getLoadedRemoteIndex(remoteObj.baseRemote);
          if (baseRemoteOrder < dependencyRemoteOrder) {
            downloadBaseScript = true;
          }
          return {
            func: () =>
              new Promise((res) => {
                res();
              })
          };
        }
      });

      for (let [index, script] of remoteScripts.entries()) {
        await script.func();
        if (baseRemoteLoading && index + 1 === remoteScripts.length) {
          Reflect.deleteProperty(window.loadingScripts, remoteObj.baseRemote);
        }
      }

      async function checkDependencyDownload() {
        return new Promise(async (resolve) => {
          async function checkDownload(module) {
            if (
              window.loadingScripts[module] &&
              !window.loadedRemotes.includes(module)
            ) {
              await delay(200);
              await checkDependencyDownload();
            }
            resolve();
          }
          if (dependsOn.length === 0) {
            await checkDownload(remoteObj.baseRemote);
          } else {
            for (let sc of dependsOn) {
              await checkDownload(sc);
            }
          }
        });
      }
      await checkDependencyDownload();

      /** Deleting remoteScript from the obj */
      Object.keys(remoteScriptobj).forEach((key) => {
        if (window.loadedRemotes.includes(key)) {
          Reflect.deleteProperty(window.loadingScripts, key);
        }
      });

      /** check if script is already loading
       * this happens if same route is loaded via lazyremote
       * eg (topnav - 'team') and page route is 'team'
       **/
      if (window.loadingScripts[remoteObj.baseRemote]) {
        isBaseScriptLoaded.current = false;
        const myInterval = setInterval(() => {
          console.info("setinterval calling", remoteObj.baseRemote);
          if (!window.loadingScripts[remoteObj.baseRemote]) {
            setReady(true);
            console.info("setinterval clearing", remoteObj.baseRemote);
            clearInterval(myInterval);
          }
        }, 200);
      }

      const remoteBaseElement = document.querySelector(
        `[src="${remoteBaseUrl}"]`
      );

      /** download baseremote script */
      if (
        (!remoteBaseElement ||
          downloadBaseScript ||
          !window.loadedRemotes.includes(remoteObj.baseRemote)) &&
        !window.loadingScripts[remoteObj.baseRemote]
      ) {
        if (remoteBaseElement) remoteBaseElement.remove();
        const baseRemoteOrder = getLoadedRemoteIndex(remoteObj.baseRemote);
        if (baseRemoteOrder > -1 && downloadBaseScript) {
          window.loadedRemotes.splice(baseRemoteOrder, 1);
        }
        window.loadingScripts[remoteObj.baseRemote] = true;
        await downloadScript(remoteBaseUrl)();
        Reflect.deleteProperty(window.loadingScripts, remoteObj.baseRemote);
        window.loadedRemotes.push(remoteObj.baseRemote);
      }

      /** do not set readystate if script is loading */
      if (isBaseScriptLoaded.current) {
        setReady(true);
      }
    }

    loadScripts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    ready,
    failed
  };
};

LazyRemote.propTypes = {
  name: PropTypes.string,
  module: PropTypes.string,
  componentName: PropTypes.string,
  dependsOn: PropTypes.arrayOf(PropTypes.string)
};

export function LazyRemote(props) {
  let { name, module, componentName, dependsOn = [], ...restProps } = props;
  let { ready, failed } = useDynamicScript(name, dependsOn, componentName);
  let [Component, setComponent] = React.useState(() => () => {
    console.info("Loading remote module");
    return null;
  });

  React.useEffect(
    function loadRemoteComponent() {
      if (!ready) return;
      loadComponent(module, componentName)()
        .then((component) => {
          setComponent(() => component);
        })
        .catch((err) => {
          console.error("error in lazyremote", err, name, module);
          setComponent(() => () => (
            <p>
              Failed to load external module
              <details>
                <summary>View more</summary>
                <p>{err.toString()}</p>
              </details>
            </p>
          ));
        });
    },
    [ready, name, module, componentName]
  );

  if (failed) {
    console.error(`Failed to load remote ${name}`);
    return null;
  }

  if (!ready) {
    console.info(`Loading ${name}...`);
    return null;
  }

  return <Component {...restProps} />;
}
